/**
 * Comprehensive Error Hierarchy for the Application
 */

export const enum ErrorCode {
  // Network errors
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  OFFLINE_ERROR = 'OFFLINE_ERROR',
  
  // API errors
  API_ERROR = 'API_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  NOT_FOUND_ERROR = 'NOT_FOUND_ERROR',
  RATE_LIMIT_ERROR = 'RATE_LIMIT_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  
  // Authentication errors
  AUTH_ERROR = 'AUTH_ERROR',
  UNAUTHORIZED_ERROR = 'UNAUTHORIZED_ERROR',
  FORBIDDEN_ERROR = 'FORBIDDEN_ERROR',
  SESSION_EXPIRED_ERROR = 'SESSION_EXPIRED_ERROR',
  
  // Application errors
  APP_ERROR = 'APP_ERROR',
  CONFIG_ERROR = 'CONFIG_ERROR',
  PARSE_ERROR = 'PARSE_ERROR',
  FILE_ERROR = 'FILE_ERROR',
  
  // Study errors
  STUDY_ERROR = 'STUDY_ERROR',
  STUDY_NOT_FOUND = 'STUDY_NOT_FOUND',
  STUDY_ALREADY_EXISTS = 'STUDY_ALREADY_EXISTS',
  STUDY_LIMIT_EXCEEDED = 'STUDY_LIMIT_EXCEEDED',
  
  // Unknown
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export const enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export interface ErrorMetadata {
  timestamp: Date
  requestId?: string
  userId?: string
  context?: Record<string, unknown>
  retryCount?: number
  recoverable?: boolean
}

/**
 * Base error class for all application errors
 */
export abstract class BaseError extends Error {
  public readonly code: ErrorCode
  public readonly severity: ErrorSeverity
  public readonly metadata: ErrorMetadata
  public readonly originalError?: Error

  constructor(
    message: string,
    code: ErrorCode,
    severity: ErrorSeverity,
    metadata?: Partial<ErrorMetadata>,
    originalError?: Error
  ) {
    super(message)
    this.name = this.constructor.name
    this.code = code
    this.severity = severity
    this.metadata = {
      timestamp: new Date(),
      recoverable: true,
      ...metadata
    }
    this.originalError = originalError

    // Maintain proper stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor)
    }
  }

  /**
   * Convert error to a user-friendly message
   */
  abstract getUserMessage(): string

  /**
   * Determine if the error is recoverable
   */
  isRecoverable(): boolean {
    return this.metadata.recoverable ?? true
  }

  /**
   * Get suggested action for the user
   */
  abstract getSuggestedAction(): string

  /**
   * Convert to plain object for logging/serialization
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      code: this.code,
      message: this.message,
      severity: this.severity,
      metadata: this.metadata,
      stack: this.stack,
      originalError: this.originalError ? {
        name: this.originalError.name,
        message: this.originalError.message,
        stack: this.originalError.stack
      } : undefined
    }
  }
}

/**
 * Network-related errors
 */
export class NetworkError extends BaseError {
  constructor(message: string, metadata?: Partial<ErrorMetadata>, originalError?: Error) {
    super(message, ErrorCode.NETWORK_ERROR, ErrorSeverity.HIGH, metadata, originalError)
  }

  getUserMessage(): string {
    return 'Network connection issue. Please check your internet connection and try again.'
  }

  getSuggestedAction(): string {
    return 'Check your internet connection and refresh the page.'
  }
}

export class TimeoutError extends BaseError {
  constructor(timeoutMs: number, metadata?: Partial<ErrorMetadata>) {
    super(
      `Request timed out after ${timeoutMs}ms`,
      ErrorCode.TIMEOUT_ERROR,
      ErrorSeverity.HIGH,
      { ...metadata, context: { timeoutMs } }
    )
  }

  getUserMessage(): string {
    return 'The request took too long to complete. The server might be busy.'
  }

  getSuggestedAction(): string {
    return 'Please wait a moment and try again. If the problem persists, contact support.'
  }
}

export class OfflineError extends BaseError {
  constructor(metadata?: Partial<ErrorMetadata>) {
    super(
      'No internet connection',
      ErrorCode.OFFLINE_ERROR,
      ErrorSeverity.HIGH,
      { ...metadata, recoverable: false }
    )
  }

  getUserMessage(): string {
    return 'You appear to be offline. Please check your internet connection.'
  }

  getSuggestedAction(): string {
    return 'Connect to the internet and refresh the page.'
  }
}

/**
 * API-related errors
 */
export class ApiError extends BaseError {
  public readonly statusCode?: number
  public readonly endpoint?: string
  public readonly debugInfo?: any

  constructor(
    message: string,
    statusCode?: number,
    endpointOrDebugInfo?: string | any,
    metadata?: Partial<ErrorMetadata>,
    originalError?: Error
  ) {
    const severity = statusCode && statusCode >= 500 ? ErrorSeverity.HIGH : ErrorSeverity.MEDIUM
    
    // Handle overloaded parameter - can be endpoint string or debug info object
    let endpoint: string | undefined
    let debugInfo: any
    if (typeof endpointOrDebugInfo === 'string') {
      endpoint = endpointOrDebugInfo
    } else {
      debugInfo = endpointOrDebugInfo
    }
    
    super(
      message,
      ErrorCode.API_ERROR,
      severity,
      { ...metadata, context: { statusCode, endpoint, ...debugInfo } },
      originalError
    )
    this.statusCode = statusCode
    this.endpoint = endpoint
    this.debugInfo = debugInfo
  }

  getUserMessage(): string {
    if (this.statusCode) {
      if (this.statusCode >= 500) {
        return 'Server error occurred. Our team has been notified.'
      }
      if (this.statusCode === 404) {
        return 'The requested resource was not found.'
      }
      if (this.statusCode === 429) {
        return 'Too many requests. Please slow down and try again.'
      }
    }
    return 'An error occurred while communicating with the server.'
  }

  getSuggestedAction(): string {
    if (this.statusCode && this.statusCode >= 500) {
      return 'Please try again later. If the problem persists, contact support.'
    }
    return 'Please try again. If the problem persists, refresh the page.'
  }
}

export class ValidationError extends BaseError {
  public readonly validationErrors?: Record<string, string[]>

  constructor(
    message: string,
    validationErrors?: Record<string, string[]>,
    metadata?: Partial<ErrorMetadata>
  ) {
    super(
      message,
      ErrorCode.VALIDATION_ERROR,
      ErrorSeverity.LOW,
      metadata
    )
    this.validationErrors = validationErrors
  }

  getUserMessage(): string {
    return 'Please check your input and try again.'
  }

  getSuggestedAction(): string {
    return 'Review the highlighted fields and correct any errors.'
  }
}

export class NotFoundError extends BaseError {
  constructor(resource: string, metadata?: Partial<ErrorMetadata>) {
    super(
      `${resource} not found`,
      ErrorCode.NOT_FOUND_ERROR,
      ErrorSeverity.MEDIUM,
      metadata
    )
  }

  getUserMessage(): string {
    return 'The requested item could not be found.'
  }

  getSuggestedAction(): string {
    return 'Please verify the URL or try searching again.'
  }
}

export class RateLimitError extends BaseError {
  public readonly retryAfter?: number

  constructor(retryAfter?: number, metadata?: Partial<ErrorMetadata>) {
    super(
      'Rate limit exceeded',
      ErrorCode.RATE_LIMIT_ERROR,
      ErrorSeverity.MEDIUM,
      { ...metadata, context: { retryAfter } }
    )
    this.retryAfter = retryAfter
  }

  getUserMessage(): string {
    if (this.retryAfter) {
      return `Rate limit exceeded. Please try again in ${Math.ceil(this.retryAfter / 1000)} seconds.`
    }
    return 'Too many requests. Please slow down and try again.'
  }

  getSuggestedAction(): string {
    return 'Wait a moment before making more requests.'
  }
}

/**
 * Authentication errors
 */
export class AuthError extends BaseError {
  constructor(message: string, metadata?: Partial<ErrorMetadata>, originalError?: Error) {
    super(message, ErrorCode.AUTH_ERROR, ErrorSeverity.HIGH, metadata, originalError)
  }

  getUserMessage(): string {
    return 'Authentication error occurred.'
  }

  getSuggestedAction(): string {
    return 'Please sign in again.'
  }
}

export class UnauthorizedError extends BaseError {
  constructor(metadata?: Partial<ErrorMetadata>) {
    super(
      'Unauthorized access',
      ErrorCode.UNAUTHORIZED_ERROR,
      ErrorSeverity.HIGH,
      metadata
    )
  }

  getUserMessage(): string {
    return 'You need to sign in to access this resource.'
  }

  getSuggestedAction(): string {
    return 'Please sign in with your account.'
  }
}

export class ForbiddenError extends BaseError {
  constructor(resource?: string, metadata?: Partial<ErrorMetadata>) {
    super(
      `Access forbidden${resource ? ` to ${resource}` : ''}`,
      ErrorCode.FORBIDDEN_ERROR,
      ErrorSeverity.HIGH,
      metadata
    )
  }

  getUserMessage(): string {
    return "You don't have permission to access this resource."
  }

  getSuggestedAction(): string {
    return 'Contact your administrator if you believe you should have access.'
  }
}

export class SessionExpiredError extends BaseError {
  constructor(metadata?: Partial<ErrorMetadata>) {
    super(
      'Session expired',
      ErrorCode.SESSION_EXPIRED_ERROR,
      ErrorSeverity.HIGH,
      metadata
    )
  }

  getUserMessage(): string {
    return 'Your session has expired.'
  }

  getSuggestedAction(): string {
    return 'Please sign in again to continue.'
  }
}

/**
 * Application errors
 */
export class AppError extends BaseError {
  constructor(
    message: string,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    metadata?: Partial<ErrorMetadata>,
    originalError?: Error
  ) {
    super(message, ErrorCode.APP_ERROR, severity, metadata, originalError)
  }

  getUserMessage(): string {
    return 'An application error occurred.'
  }

  getSuggestedAction(): string {
    return 'Please refresh the page and try again.'
  }
}

export class ConfigError extends BaseError {
  constructor(message: string, metadata?: Partial<ErrorMetadata>) {
    super(
      message,
      ErrorCode.CONFIG_ERROR,
      ErrorSeverity.HIGH,
      { ...metadata, recoverable: false }
    )
  }

  getUserMessage(): string {
    return 'Configuration error detected.'
  }

  getSuggestedAction(): string {
    return 'Please contact support to resolve this issue.'
  }
}

export class ParseError extends BaseError {
  constructor(
    message: string,
    dataType?: string,
    metadata?: Partial<ErrorMetadata>,
    originalError?: Error
  ) {
    super(
      message,
      ErrorCode.PARSE_ERROR,
      ErrorSeverity.LOW,
      { ...metadata, context: { dataType } },
      originalError
    )
  }

  getUserMessage(): string {
    return 'Failed to process the data. Please check the format.'
  }

  getSuggestedAction(): string {
    return 'Verify the data format and try again.'
  }
}

export class FileError extends BaseError {
  public readonly fileName?: string
  public readonly fileSize?: number

  constructor(
    message: string,
    fileName?: string,
    fileSize?: number,
    metadata?: Partial<ErrorMetadata>,
    originalError?: Error
  ) {
    super(
      message,
      ErrorCode.FILE_ERROR,
      ErrorSeverity.LOW,
      { ...metadata, context: { fileName, fileSize } },
      originalError
    )
    this.fileName = fileName
    this.fileSize = fileSize
  }

  getUserMessage(): string {
    return 'File processing error.'
  }

  getSuggestedAction(): string {
    return 'Please check the file format and size, then try again.'
  }
}

/**
 * Study-specific errors
 */
export class StudyError extends BaseError {
  public readonly studyId?: string

  constructor(
    message: string,
    studyId?: string,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    metadata?: Partial<ErrorMetadata>,
    originalError?: Error
  ) {
    super(
      message,
      ErrorCode.STUDY_ERROR,
      severity,
      { ...metadata, context: { studyId } },
      originalError
    )
    this.studyId = studyId
  }

  getUserMessage(): string {
    return 'An error occurred with the study.'
  }

  getSuggestedAction(): string {
    return 'Please check the study configuration and try again.'
  }
}

export class StudyNotFoundError extends BaseError {
  public readonly studyId: string

  constructor(studyId: string, metadata?: Partial<ErrorMetadata>) {
    super(
      `Study ${studyId} not found`,
      ErrorCode.STUDY_NOT_FOUND,
      ErrorSeverity.MEDIUM,
      { ...metadata, context: { studyId } }
    )
    this.studyId = studyId
  }

  getUserMessage(): string {
    return 'The requested study could not be found.'
  }

  getSuggestedAction(): string {
    return 'Please verify the study ID or return to the studies list.'
  }
}

export class StudyLimitExceededError extends BaseError {
  public readonly limit: number
  public readonly current: number

  constructor(limit: number, current: number, metadata?: Partial<ErrorMetadata>) {
    super(
      `Study limit exceeded: ${current}/${limit}`,
      ErrorCode.STUDY_LIMIT_EXCEEDED,
      ErrorSeverity.MEDIUM,
      { ...metadata, context: { limit, current } }
    )
    this.limit = limit
    this.current = current
  }

  getUserMessage(): string {
    return `You have reached your study limit (${this.current}/${this.limit}).`
  }

  getSuggestedAction(): string {
    return 'Delete completed studies or upgrade your plan for more studies.'
  }
}

/**
 * Error factory to create appropriate error instances
 */
export class ErrorFactory {
  static fromApiResponse(
    response: Response,
    responseText?: string,
    requestId?: string
  ): BaseError {
    const metadata: Partial<ErrorMetadata> = { requestId }

    // Try to parse the enhanced error response
    let errorData: any = null
    try {
      errorData = responseText ? JSON.parse(responseText) : null
    } catch {
      // Not JSON, use raw text
    }

    // If we have enhanced error data from backend
    if (errorData && typeof errorData === 'object') {
      // Extract debug information if available
      const debugInfo = errorData.debug
      const enhancedMetadata: Partial<ErrorMetadata> = {
        ...metadata,
        requestId: errorData.request_id || requestId,
        context: {
          timestamp: errorData.timestamp,
          path: errorData.path,
          method: errorData.method,
          environment: errorData.environment,
          queryParams: errorData.query_params,
          ...(debugInfo ? {
            exceptionType: debugInfo.exception_type,
            exceptionModule: debugInfo.exception_module,
            errorMessage: debugInfo.error_message,
            traceback: debugInfo.traceback,
            possibleCauses: debugInfo.possible_causes,
            databaseError: debugInfo.database_error,
            queueError: debugInfo.queue_error,
            originalArgs: debugInfo.args
          } : {})
        }
      }

      // Create appropriate error based on error code
      const errorCode = errorData.error
      const message = errorData.message || responseText || `HTTP ${response.status}`
      
      switch (errorCode) {
        case 'DATABASE_ERROR':
          return new ApiError(
            `Database Error: ${message}`,
            response.status || 503,
            debugInfo,
            enhancedMetadata
          )
        case 'QUEUE_ERROR':
          return new ApiError(
            `Background Processing Error: ${message}`,
            response.status || 503,
            debugInfo,
            enhancedMetadata
          )
        case 'VALIDATION_ERROR':
          return new ValidationError(
            message,
            errorData.details?.errors || {},
            enhancedMetadata
          )
        case 'OPTIMIZATION_ERROR':
          return new ApiError(
            `Optimization Error: ${message}`,
            response.status || 500,
            debugInfo,
            enhancedMetadata
          )
        case 'RESOURCE_NOT_FOUND':
          return new NotFoundError(
            errorData.details?.resource_type || 'Resource',
            enhancedMetadata
          )
        case 'RATE_LIMIT_EXCEEDED':
          return new RateLimitError(
            errorData.details?.retry_after,
            enhancedMetadata
          )
        default:
          // Use the enhanced error message and debug info
          return new ApiError(
            message,
            response.status,
            debugInfo,
            enhancedMetadata
          )
      }
    }

    // Fall back to original logic for non-enhanced responses
    switch (response.status) {
      case 401:
        return new UnauthorizedError(metadata)
      case 403:
        return new ForbiddenError(undefined, metadata)
      case 404:
        return new NotFoundError('Resource', metadata)
      case 429:
        const retryAfter = response.headers.get('Retry-After')
        return new RateLimitError(
          retryAfter ? parseInt(retryAfter, 10) : undefined,
          metadata
        )
      case 400:
        // Try to parse validation errors
        try {
          const data = responseText ? JSON.parse(responseText) : {}
          if (data.errors) {
            return new ValidationError(
              data.message || 'Validation failed',
              data.errors,
              metadata
            )
          }
        } catch {
          // Fall through to generic error
        }
        return new ApiError(
          responseText || 'Bad request',
          400,
          undefined,
          metadata
        )
      default:
        if (response.status >= 500) {
          return new ApiError(
            responseText || `Server error: ${response.status}`,
            response.status,
            undefined,
            metadata
          )
        }
        return new ApiError(
          responseText || `HTTP ${response.status}`,
          response.status,
          undefined,
          metadata
        )
    }
  }

  static fromError(error: unknown, context?: string): BaseError {
    // If it's already our error, return it
    if (error instanceof BaseError) {
      return error
    }

    // Handle standard Error instances
    if (error instanceof Error) {
      // Network errors
      if (error.name === 'NetworkError' || error.message.includes('network')) {
        return new NetworkError(error.message, undefined, error)
      }

      // Timeout errors
      if (error.name === 'AbortError' || error.message.includes('timeout')) {
        return new TimeoutError(0, undefined)
      }

      // Parse errors
      if (error.name === 'SyntaxError' || error.message.includes('JSON')) {
        return new ParseError(error.message, 'JSON', undefined, error)
      }

      // Generic app error
      return new AppError(
        error.message,
        ErrorSeverity.MEDIUM,
        { context: { originalContext: context } },
        error
      )
    }

    // Handle non-Error objects
    const message = String(error)
    return new AppError(
      message,
      ErrorSeverity.MEDIUM,
      { context: { originalContext: context, rawError: error } }
    )
  }
}